import React, { useState, useEffect, useMemo } from "react";
import {
  Upload,
  Download,
  FileText,
  Users,
  Mail,
  TrendingUp,
  AlertCircle,
  CheckCircle,
  X,
  Plus,
  Filter,
  Search,
  BarChart3,
  Calendar,
  Settings,
  RefreshCw,
  Eye,
  Edit,
  Trash2,
  AlertTriangle,
} from "lucide-react";
import useLead from "../useLead";
import { ILead } from "../Lead.props";
import Modal from "../../../../components/Model";
import Button from "../../../../components/Button";
import toast from "react-hot-toast";

interface ImportResult {
  summary: {
    successCount: number;
    errorCount: number;
    totalCount: number;
  };
  errors?: Array<{
    row: number;
    message: string;
  }>;
}

const LeadTracker: React.FC = () => {
  const {
    leadData,
    loading,
    handleImportCsv,
    openImportModal,
    closeImportModal,
    isImportModalOpen,
    importLoading,
    importResults,
    handleDateFilter,
    search,
    setSearch,
    page,
    setPage,
    totalPage,
  } = useLead();

  // Local state
  const [currentView, setCurrentView] = useState<
    "overview" | "import" | "analytics"
  >("overview");
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [importStep, setImportStep] = useState<
    "upload" | "preview" | "confirm"
  >("upload");
  const [previewData, setPreviewData] = useState<Record<string, string>[]>([]);
  const [filterStatus, setFilterStatus] = useState<string>("all");
  const [filterIndustry, setFilterIndustry] = useState<string>("all");
  const [dateRange, setDateRange] = useState<{
    start: Date | null;
    end: Date | null;
  }>({
    start: new Date(Date.now() - 30 * 24 * 60 * 60 * 1000),
    end: new Date(),
  });

  // Analytics data
  const analytics = useMemo(() => {
    if (!leadData) return null;

    const totalLeads = leadData.length;
    const activeLeads = leadData.filter(
      (lead) => lead.status === "Active"
    ).length;
    const emailSent = leadData.filter((lead) => lead.isEmailSent).length;
    const replied = leadData.filter(
      (lead) => lead.leadStatus === "replied"
    ).length;

    // Industry breakdown
    const industryStats = leadData.reduce(
      (acc: Record<string, number>, lead) => {
        const industry = lead.services?.type || "Unknown";
        acc[industry] = (acc[industry] || 0) + 1;
        return acc;
      },
      {} as Record<string, number>
    );

    // Status breakdown
    const statusStats = leadData.reduce((acc: Record<string, number>, lead) => {
      acc[lead.leadStatus] = (acc[lead.leadStatus] || 0) + 1;
      return acc;
    }, {} as Record<string, number>);

    return {
      totalLeads,
      activeLeads,
      emailSent,
      replied,
      responseRate:
        emailSent > 0 ? ((replied / emailSent) * 100).toFixed(1) : 0,
      industryStats,
      statusStats,
    };
  }, [leadData]);

  // Filtered leads
  const filteredLeads = useMemo(() => {
    if (!leadData) return [];

    return leadData.filter((lead) => {
      const matchesSearch =
        !search ||
        lead.linkedin?.toLowerCase().includes(search.toLowerCase()) ||
        lead.email?.toLowerCase().includes(search.toLowerCase()) ||
        lead.companyName?.toLowerCase().includes(search.toLowerCase());

      const matchesStatus =
        filterStatus === "all" || lead.leadStatus === filterStatus;
      const matchesIndustry =
        filterIndustry === "all" || lead.services?.type === filterIndustry;

      return matchesSearch && matchesStatus && matchesIndustry;
    });
  }, [leadData, search, filterStatus, filterIndustry]);

  // Handle file selection
  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      setSelectedFile(file);
      previewCsvFile(file);
    }
  };

  // Preview CSV file
  const previewCsvFile = (file: File) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const csv = e.target?.result as string;
        const lines = csv.split("\n");
        const headers = lines[0]
          .split(",")
          .map((h) => h.trim().replace(/"/g, ""));
        const data = lines.slice(1, 6).map((line) => {
          const values = line.split(",").map((v) => v.trim().replace(/"/g, ""));
          const row: any = {};
          headers.forEach((header, index) => {
            row[header] = values[index] || "";
          });
          return row;
        });
        setPreviewData(data);
        setImportStep("preview");
      } catch (error) {
        toast.error("Error reading CSV file");
      }
    };
    reader.readAsText(file);
  };

  // Handle import
  const handleImport = async () => {
    if (!selectedFile) return;

    try {
      // Log the CSV data being sent for debugging
      console.log("CSV Preview Data:", previewData);
      console.log(
        "CSV Headers:",
        previewData[0] ? Object.keys(previewData[0]) : []
      );
      console.log(
        "Selected File:",
        selectedFile.name,
        selectedFile.size,
        selectedFile.type
      );

      // Validate that required fields are present (case-insensitive)
      if (previewData.length > 0) {
        const headers = Object.keys(previewData[0]);
        const headerMap: Record<string, string> = {};

        // Create a mapping of lowercase headers to actual headers
        headers.forEach((header) => {
          headerMap[header.toLowerCase()] = header;
        });

        // Define required fields with exact names (case-sensitive)
        const requiredFieldMappings = {
          linkedin: ["linkedin"],
          email: ["email"],
          companyName: ["companyName"],
          description: ["description"],
          leadStatus: ["leadStatus"],
        };

        const missingFields: string[] = [];

        // Check each required field (exact match)
        Object.entries(requiredFieldMappings).forEach(
          ([requiredField, possibleNames]) => {
            const found = possibleNames.some((name) => headers.includes(name));
            if (!found) {
              missingFields.push(requiredField);
            }
          }
        );

        if (missingFields.length > 0) {
          toast.error(
            `Missing required fields: ${missingFields.join(
              ", "
            )}. Please ensure your CSV has columns for: linkedin, email, companyName, description, leadStatus`
          );
          return;
        }

        // Check if description field has data
        const hasDescriptionData = previewData.some(
          (row) => row.description && row.description.trim() !== ""
        );
        if (!hasDescriptionData) {
          toast(
            "Warning: Description field is empty for some or all rows. This may affect lead display.",
            { icon: "⚠️" }
          );
        }

        // Check if linkedin field contains valid LinkedIn URLs
        const invalidLinkedinData = previewData.filter(
          (row) =>
            row.linkedin &&
            (!row.linkedin.includes("linkedin.com") ||
              row.linkedin.includes("docs.google.com") ||
              row.linkedin.includes("spreadsheets"))
        );
        if (invalidLinkedinData.length > 0) {
          toast.error(
            "Error: The linkedin field should contain valid LinkedIn profile URLs. Please export your data as CSV from Google Sheets instead of copying directly."
          );
          return;
        }

        // Check if leadStatus is empty
        const emptyLeadStatus = previewData.filter(
          (row) => !row.leadStatus || row.leadStatus.trim() === ""
        );
        if (emptyLeadStatus.length > 0) {
          toast.error(
            "Error: leadStatus field cannot be empty. Please provide a valid status for all rows."
          );
          return;
        }

        // Check if leadStatus values are valid
        const validLeadStatuses = [
          "New",
          "Send For Review",
          "Approved",
          "Skip",
          "Rejected",
          "Contacted",
          "Converted",
          "Lost",
          "Duplicate",
          "Not Interested",
          "On Hold",
          "Reminder",
        ];
        const invalidLeadStatuses = previewData.filter(
          (row) => row.leadStatus && !validLeadStatuses.includes(row.leadStatus)
        );
        if (invalidLeadStatuses.length > 0) {
          toast.error(
            `Invalid leadStatus values found. Valid values are: ${validLeadStatuses.join(
              ", "
            )}`
          );
          return;
        }

        console.log("CSV Headers found:", headers);
        console.log("Header mapping:", headerMap);
        console.log("Sample row data:", previewData[0]);
      }

      // Log the actual file content for debugging
      const fileReader = new FileReader();
      fileReader.onload = (e) => {
        const content = e.target?.result as string;
        console.log(
          "File content (first 500 chars):",
          content.substring(0, 500)
        );
      };
      fileReader.readAsText(selectedFile);

      await handleImportCsv(selectedFile);
      setImportStep("upload");
      setSelectedFile(null);
      setPreviewData([]);
    } catch (error) {
      console.error("Import failed:", error);
    }
  };

  // Download template
  const downloadTemplate = () => {
    const template = [
      "linkedin,email,phone,companyName,description,link,comments,leadStatus",
      "https://linkedin.com/in/johndoe,john@example.com,+1234567890,Example Corp,Looking for web development services for e-commerce platform,https://example.com,Follow up needed,New",
    ].join("\n");

    const blob = new Blob([template], { type: "text/csv" });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "lead_import_template.csv";
    a.click();
    window.URL.revokeObjectURL(url);
  };

  // Export filtered data
  const exportData = () => {
    if (!filteredLeads.length) {
      toast.error("No data to export");
      return;
    }

    const csvData = [
      [
        "LinkedIn",
        "Email",
        "Phone",
        "Company",
        "Status",
        "Created Date",
        "Description",
        "Link",
        "Comments",
      ],
      ...filteredLeads.map((lead) => [
        lead.linkedin || "",
        lead.email || "",
        lead.phone || "",
        lead.companyName || "",
        lead.leadStatus || "",
        new Date(lead.createdAt || "").toLocaleDateString(),
        lead.description || "",
        lead.link || "",
        lead.comments || "",
      ]),
    ]
      .map((row) => row.join(","))
      .join("\n");

    const blob = new Blob([csvData], { type: "text/csv" });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `leads_export_${new Date().toISOString().split("T")[0]}.csv`;
    a.click();
    window.URL.revokeObjectURL(url);
  };

  return (
    <div className="h-screen bg-gradient-to-br from-blue-50 to-indigo-100 flex flex-col">
      {/* Header */}
      <div className="bg-white shadow-sm border-b flex-shrink-0">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center space-x-4">
              <div className="flex items-center">
                <Users className="w-8 h-8 text-blue-600" />
                <h1 className="ml-2 text-xl font-bold text-gray-900">
                  Lead Tracker
                </h1>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 flex-1 flex flex-col">
        {currentView === "overview" && (
          <div className="space-y-4 flex-1 flex flex-col">         
            {/* Filters and Search */}
            <div className="bg-white rounded-xl shadow-sm border p-6">
              <div className="flex flex-col lg:flex-row gap-4">
                <div className="flex-1">
                  <div className="relative">
                    <Search className="w-5 h-5 absolute left-3 top-3 text-gray-400" />
                    <input
                      type="text"
                      placeholder="Search leads..."
                      value={search}
                      onChange={(e) => setSearch(e.target.value)}
                      className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                  </div>
                </div>

                <div className="flex gap-3">
                  <select
                    value={filterStatus}
                    onChange={(e) => setFilterStatus(e.target.value)}
                    className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  >
                    <option value="all">All Statuses</option>
                    <option value="New">New</option>
                    <option value="Send For Review">Send For Review</option>
                    <option value="Approved">Approved</option>
                    <option value="Skip">Skip</option>
                    <option value="Rejected">Rejected</option>
                    <option value="Contacted">Contacted</option>
                    <option value="Converted">Converted</option>
                    <option value="Lost">Lost</option>
                    <option value="Duplicate">Duplicate</option>
                    <option value="Not Interested">Not Interested</option>
                    <option value="On Hold">On Hold</option>
                    <option value="Reminder">Reminder</option>
                  </select>

                

                  <button
                    onClick={openImportModal}
                    className="flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                  >
                    <Upload className="w-4 h-4 mr-2" />
                    Import CSV File
                  </button>
                  <button
                    onClick={exportData}
                    className="flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                  >
                    <Download className="w-4 h-4 mr-2" />
                    Export
                  </button>
                </div>
              </div>
            </div>

            

            {/* Leads Table */}
            <div className="bg-white rounded-xl shadow-sm border overflow-hidden flex-1 flex flex-col">
              <div className="px-6 py-4 border-b border-gray-200 flex-shrink-0">
                <div className="flex items-center justify-between">
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900">
                      Today's Leads
                    </h3>
                    {totalPage > 1 && (
                      <p className="text-sm text-gray-500 mt-1">
                        Page {page} of {totalPage} • {leadData?.length || 0} total leads
                      </p>
                    )}
                  </div>
                  <div className="flex items-center space-x-2">
                    <button
                      onClick={() => {
                        const today = new Date();
                        handleDateFilter(today, today);
                      }}
                      className="flex items-center px-3 py-1 text-sm bg-blue-100 text-blue-700 rounded-md hover:bg-blue-200 transition-colors"
                    >
                      <RefreshCw className="w-4 h-4 mr-1" />
                      Refresh
                    </button>
                    <span className="text-sm text-gray-500">
                      {new Date().toLocaleDateString()}
                    </span>
                  </div>
                </div>
              </div>

              <div className="overflow-auto flex-1">
                <table className="w-full min-w-full">
                  <thead className="bg-gray-50 sticky top-0 z-10">
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-32">
                        LinkedIn
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-40">
                        Company
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-48">
                        Email
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-64">
                        Description
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-32">
                        Status
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-32">
                        Services
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-32">
                        Created
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {loading ? (
                      <tr>
                        <td colSpan={7} className="px-6 py-4 text-center">
                          <div className="flex items-center justify-center">
                            <RefreshCw className="w-5 h-5 animate-spin text-blue-600 mr-2" />
                            Loading leads...
                          </div>
                        </td>
                      </tr>
                    ) : filteredLeads.length === 0 ? (
                      <tr>
                        <td
                          colSpan={7}
                          className="px-6 py-8 text-center text-gray-500"
                        >
                          No leads found. Try adjusting your filters or import
                          some leads.
                        </td>
                      </tr>
                    ) : (
                      filteredLeads.map((lead: ILead) => (
                        <tr key={lead._id} className="hover:bg-gray-50">
                          <td className="px-4 py-3 whitespace-nowrap">
                            <div className="text-sm font-medium text-gray-900">
                              {lead.linkedin ? (
                                <a
                                  href={lead.linkedin}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="text-blue-600 hover:text-blue-800 underline"
                                >
                                  {lead.name}
                                </a>
                              ) : (
                                "-"
                              )}
                            </div>
                          </td>
                          <td className="px-4 py-3 whitespace-nowrap">
                            <div
                              className="text-sm text-gray-900 max-w-40 truncate"
                              title={lead.companyName || ""}
                            >
                              {lead.companyName || "-"}
                            </div>
                          </td>
                          <td className="px-4 py-3 whitespace-nowrap">
                            <div
                              className="text-sm text-gray-900 max-w-48 truncate"
                              title={lead.email || ""}
                            >
                              {lead.email || "-"}
                            </div>
                          </td>
                          <td className="px-4 py-3">
                            <div
                              className="text-sm text-gray-900 max-w-64 overflow-hidden"
                              style={{
                                display: "-webkit-box",
                                WebkitLineClamp: 2,
                                WebkitBoxOrient: "vertical",
                              }}
                              title={lead.description || ""}
                            >
                              {lead.description || "-"}
                            </div>
                          </td>
                          <td className="px-4 py-3 whitespace-nowrap">
                            <span
                              className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                                lead.leadStatus === "approved"
                                  ? "bg-green-100 text-green-800"
                                  : lead.leadStatus === "replied"
                                  ? "bg-blue-100 text-blue-800"
                                  : "bg-yellow-100 text-yellow-800"
                              }`}
                            >
                              {lead.leadStatus}
                            </span>
                          </td>
                          <td className="px-4 py-3 whitespace-nowrap">
                            <div
                              className="text-sm text-gray-900 max-w-32 truncate"
                              title={lead.services?.type || ""}
                            >
                              {lead.services?.type || "-"}
                            </div>
                          </td>
                          <td className="px-4 py-3 whitespace-nowrap">
                            <div className="text-sm text-gray-500">
                              {lead.createdAt
                                ? new Date(lead.createdAt).toLocaleDateString()
                                : "-"}
                            </div>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
              
            </div>
            {/* Pagination Controls */}
            {totalPage > 1 && (
              <div className="bg-white rounded-xl shadow-sm border p-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-2 text-sm text-gray-700">
                    <span>
                      Showing page <span className="font-semibold">{page}</span> of{" "}
                      <span className="font-semibold">{totalPage}</span>
                    </span>
                    
                  </div>
                  
                  {/* Pagination Buttons */}
                <div className="flex items-center justify-center space-x-2 mt-4">
                  {/* First Page */}
                  <button
                    onClick={() => setPage(1)}
                    disabled={page === 1 || loading}
                    className="px-3 py-2 text-sm font-medium text-gray-500 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                  >
                    {loading && page === 1 ? (
                      <RefreshCw className="w-4 h-4 animate-spin" />
                    ) : (
                      "First"
                    )}
                  </button>

                  {/* Previous Page */}
                  <button
                    onClick={() => setPage(page - 1)}
                    disabled={page === 1 || loading}
                    className="px-3 py-2 text-sm font-medium text-gray-500 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                  >
                    {loading && page === page - 1 ? (
                      <RefreshCw className="w-4 h-4 animate-spin" />
                    ) : (
                      "Previous"
                    )}
                  </button>

                  {/* Page Numbers */}
                  <div className="flex items-center space-x-1">
                    {Array.from({ length: Math.min(5, totalPage) }, (_, i) => {
                      let pageNum:any;
                      if (totalPage <= 5) {
                        pageNum = i + 1;
                      } else if (page <= 3) {
                        pageNum = i + 1;
                      } else if (page >= totalPage - 2) {
                        pageNum = totalPage - 4 + i;
                      } else {
                        pageNum = page - 2 + i;
                      }

                      return (
                        <button
                          key={pageNum}
                          onClick={() => setPage(pageNum)}
                          disabled={loading}
                          className={`px-3 py-2 text-sm font-medium rounded-md transition-colors ${
                            page === pageNum
                              ? "bg-blue-600 text-white border border-blue-600"
                              : "text-gray-500 bg-white border border-gray-300 hover:bg-gray-50"
                          } disabled:opacity-50 disabled:cursor-not-allowed`}
                        >
                          {loading && page === pageNum ? (
                            <RefreshCw className="w-4 h-4 animate-spin" />
                          ) : (
                            pageNum
                          )}
                        </button>
                      );
                    })}
                  </div>

                  {/* Next Page */}
                  <button
                    onClick={() => setPage(page + 1)}
                    disabled={page === totalPage || loading}
                    className="px-3 py-2 text-sm font-medium text-gray-500 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                  >
                    {loading && page === page + 1 ? (
                      <RefreshCw className="w-4 h-4 animate-spin" />
                    ) : (
                      "Next"
                    )}
                  </button>

                  {/* Last Page */}
                  <button
                    onClick={() => setPage(totalPage)}
                    disabled={page === totalPage || loading}
                    className="px-3 py-2 text-sm font-medium text-gray-500 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                  >
                    {loading && page === totalPage ? (
                      <RefreshCw className="w-4 h-4 animate-spin" />
                    ) : (
                      "Last"
                    )}
                  </button>
                </div>
                </div>

                

                {/* Page Jump */}
                {/* <div className="flex items-center justify-center space-x-2 mt-4">
                  <span className="text-sm text-gray-700">Go to page:</span>
                  <input
                    type="number"
                    min={1}
                    max={totalPage}
                    placeholder={page.toString()}
                    onKeyPress={(e) => {
                      if (e.key === 'Enter') {
                        const input = e.target as HTMLInputElement;
                        const newPage = parseInt(input.value);
                        if (newPage >= 1 && newPage <= totalPage) {
                          setPage(newPage);
                          input.value = '';
                        }
                      }
                    }}
                    className="w-16 px-2 py-1 text-sm border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                  <button
                    onClick={() => {
                      const input = document.querySelector('input[type="number"]') as HTMLInputElement;
                      if (input) {
                        const newPage = parseInt(input.value);
                        if (newPage >= 1 && newPage <= totalPage) {
                          setPage(newPage);
                          input.value = '';
                        }
                      }
                    }}
                    className="px-3 py-1 text-sm bg-blue-600 text-white rounded hover:bg-blue-700 transition-colors"
                  >
                    Go
                  </button>
                  <span className="text-sm text-gray-700">of {totalPage}</span>
                </div> */}
              </div>
            )}
          </div>
          
        )}
      </div>

      {/* Import Modal */}
      <Modal
        isOpen={isImportModalOpen}
        onClose={closeImportModal}
        style="max-w-2xl w-full mx-4"
      >
        <div className="p-6">
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-xl font-semibold text-gray-900">
              Import CSV File
            </h2>
            {/* <button
              onClick={closeImportModal}
              className="text-gray-400 hover:text-gray-600"
            >
              <X className="w-6 h-6" />
            </button> */}
          </div>

          {importStep === "upload" && (
            <div className="space-y-6">
              <div className="border-2 border-dashed border-gray-300 rounded-lg p-8 text-center">
                <Upload className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                <h3 className="text-lg font-medium text-gray-900 mb-2">
                  Upload CSV File
                </h3>
                <p className="text-gray-500 mb-4">
                  Select a CSV file with lead data. Make sure it follows the
                  template format. The first column should be LinkedIn profile
                  URLs.
                </p>

                <div className="flex flex-col sm:flex-row gap-3 items-center justify-center">
                  <input
                    type="file"
                    accept=".csv"
                    onChange={handleFileSelect}
                    className="hidden"
                    id="csv-upload"
                  />
                  <label
                    htmlFor="csv-upload"
                    className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 cursor-pointer transition-colors text-center"
                  >
                    Choose File
                  </label>
                </div>
              </div>
            </div>
          )}

          {importStep === "preview" && (
            <div className="space-y-6">
              <div className="bg-blue-50 p-4 rounded-lg">
                <div className="flex items-center">
                  <FileText className="w-5 h-5 text-blue-600 mr-2" />
                  <span className="text-blue-800 font-medium">
                    File Preview
                  </span>
                </div>
                <p className="text-blue-700 text-sm mt-1">
                  Previewing first 5 rows of your CSV file
                </p>
                <div className="mt-2 p-2 bg-yellow-50 border border-yellow-200 rounded">
                  <p className="text-yellow-800 text-xs">
                    <strong>Note:</strong> Ensure the linkedin field contains
                    valid LinkedIn URLs and the description field contains
                    meaningful content.
                  </p>
                </div>
              </div>

              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-gray-50">
                    <tr>
                      {previewData[0] &&
                        Object.keys(previewData[0]).map((header) => (
                          <th
                            key={header}
                            className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase"
                          >
                            {header}
                          </th>
                        ))}
                    </tr>
                  </thead>
                  <tbody>
                    {previewData.map((row, index) => (
                      <tr key={index} className="border-t">
                        {Object.values(row).map((value, cellIndex) => (
                          <td
                            key={cellIndex}
                            className="px-3 py-2 text-gray-900"
                          >
                            {value || ""}
                          </td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              <div className="flex justify-between">
                <button
                  onClick={() => {
                    setImportStep("upload");
                    setSelectedFile(null);
                    setPreviewData([]);
                  }}
                  className="px-4 py-2 text-gray-600 hover:text-gray-800"
                >
                  Back
                </button>
                <button
                  onClick={handleImport}
                  disabled={importLoading}
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  {importLoading ? (
                    <div className="flex items-center">
                      <RefreshCw className="w-4 h-4 animate-spin mr-2" />
                      Importing...
                    </div>
                  ) : (
                    "Import Data"
                  )}
                </button>
              </div>
            </div>
          )}
        </div>
      </Modal>
    </div>
  );
};

export default LeadTracker;
